/**
 * OnboardingIntro.tsx
 * Welcome screen for the Companion AI onboarding flow.
 * Displays introductory message and 4 key benefits with icons.
 * Users can start the onboarding or skip for later.
 */
import React from "react";
import { View, StyleSheet } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import OnboardingBenefitCard from "./common/OnboardingBenefitCard";
import { colors, spacing, spacingStyles, textColorStyles } from "../../../styles/design-system";
import { LockIcon, BookOpenIcon, ChatsIcon, LightbulbIcon } from "@phosphor-icons/react";

export interface OnboardingIntroProps {
  onStart: () => void; // Callback to proceed to onboarding steps
  onSkip: () => void; // Callback to dismiss onboarding
}

function OnboardingIntro({ onStart, onSkip }: OnboardingIntroProps) {
  return (
    <View style={[spacingStyles.pXl, { alignItems: "center" }]}>
      <CustomText
        text="companion_onboarding_intro_title"
        size="xxl"
        weight="bold"
        style={[{ textAlign: "center", marginBottom: spacing.xs }]}
      />
      <CustomText
        text="companion_onboarding_intro_subtitle"
        size="m"
        style={[textColorStyles.textLight, { textAlign: "center", marginBottom: spacing.xl }]}
      />

      <View style={[{ width: "100%", gap: spacing.md, marginBottom: spacing.xl }]}>
        <OnboardingBenefitCard
          icon={<LockIcon size={20} weight="regular" color={colors.success[600]} />}
          titleKey="companion_onboarding_intro_benefit_1"
          subtitleKey="companion_onboarding_intro_benefit_1_subtitle"
          iconBackgroundColor={colors.success[100]}
        />
        <OnboardingBenefitCard
          icon={<BookOpenIcon size={20} weight="regular" color={colors.blue[600]} />}
          titleKey="companion_onboarding_intro_benefit_2"
          subtitleKey="companion_onboarding_intro_benefit_2_subtitle"
          iconBackgroundColor={colors.blue[100]}
        />
        <OnboardingBenefitCard
          icon={<ChatsIcon size={20} weight="regular" color={colors.purple[600]} />}
          titleKey="companion_onboarding_intro_benefit_3"
          subtitleKey="companion_onboarding_intro_benefit_3_subtitle"
          iconBackgroundColor={colors.purple[100]}
        />
        <OnboardingBenefitCard
          icon={<LightbulbIcon size={20} weight="regular" color={colors.warning[500]} />}
          titleKey="companion_onboarding_intro_benefit_4"
          subtitleKey="companion_onboarding_intro_benefit_4_subtitle"
          iconBackgroundColor={colors.warning[100]}
        />
      </View>

      <CustomButton
        text="buttons.companion_onboarding_intro_start"
        onPress={onStart}
        styleType="primary"
        style={[{ width: "100%", marginBottom: spacing.md }]}
      />
      <CustomButton
        text="buttons.companion_onboarding_intro_skip"
        onPress={onSkip}
        styleType="primaryTransparent"
        style={{ width: "100%" }}
      />
    </View>
  );
}

export default OnboardingIntro;
